import React, { useState } from 'react'
import { message, Table, TableColumnsType } from 'antd'
import {
  Box,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import dayjs from 'dayjs'
import EditIcon from '@mui/icons-material/Edit'

import { IFilterMapped } from './PipelineFilter'
import {
  ONLY_DATE_FORMAT,
  ONLY_TIME_FORMAT,
} from '../../../../../constants/global'
import { axiosInstance } from '../../../../../hooks/useAxios'
import CircularLoading from '../../../../../components/CircularProgress'
import { PipelineFormModal } from './PipelineFormModal'
import { Tag } from '../../../../../components/Tag'
import Pagination from '../../../../../components/Pagination'
import { IPipeline } from '../../../../../types/pipeline'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../constants/errors'

interface IPipelineTableProps {
  isLoading: boolean
  data?: IPipeline[]
  onUpdatePipelineListSuccess: () => void
  filter: IFilterMapped
  onSearch: (filter: IFilterMapped) => void
  handlePageChange: (filter: IFilterMapped) => void
  total: number
  mutate?: () => void
}

const PipelineTable = ({
  isLoading,
  data,
  onUpdatePipelineListSuccess,
  filter,
  onSearch,
  handlePageChange,
  total,
  mutate,
}: IPipelineTableProps) => {
  const [isModalEditPipeline, setIsModalEditPipeline] = useState<boolean>(false)
  const [pipelineEdit, setPipelineEdit] = useState<IPipeline>()
  const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false)
  const onHandleDeletePipeline = async (pipeline: IPipeline) => {
    setIsLoadingDelete(true)
    try {
      await axiosInstance.delete(`/pipeline/${pipeline.objectId}`)
      onUpdatePipelineListSuccess()
      message.success(`ลบข้อมูลสำเร็จ`)
      mutate?.()
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
    setIsLoadingDelete(false)
  }

  const columns: TableColumnsType<IPipeline & { key: number }> = [
    {
      title: 'ลำดับ',
      dataIndex: 'numberRow',
      key: 'numberRow',
      align: 'center' as const,
      width: 80,
      render: (numberRow: number) => {
        return (
          <Box>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              textAlign={'center'}
            >
              {numberRow}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'ชื่อ PIPELINE',
      dataIndex: 'name',
      key: 'name',
      align: 'center' as const,
      render: (name: string) => {
        return (
          <Box sx={{ minWidth: 150, maxWidth: 400, textAlign: 'left' }}>
            <Typography variant="subtitle1" color="text.secondary">
              {name}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'สถานะของการใช้งาน',
      dataIndex: 'leadObjectIds',
      key: 'leadObjectIds',
      align: 'center' as const,
      width: 200,
      render: (leadObjectIds?: string[]) => {
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Tag color={leadObjectIds?.length! > 0 ? 'primary' : 'warning'}>
              {leadObjectIds?.length! > 0 ? (
                <Typography variant="subtitle2">
                  ถูกนำไปใช้งานแล้วจาก LEAD
                </Typography>
              ) : (
                <em>ยังไม่ใช้งาน</em>
              )}
            </Tag>
          </Box>
        )
      },
    },
    {
      title: 'สถานะ',
      dataIndex: 'deletedAt',
      key: 'deletedAt',
      align: 'center' as const,
      width: 160,
      render: (deletedAt: string) => {
        return (
          <Box>
            <Tag color={deletedAt ? 'error' : 'success'}>
              {deletedAt ? 'ยกเลิก' : 'ใช้งาน'}
            </Tag>
          </Box>
        )
      },
    },
    {
      title: 'เพิ่มเมื่อ',
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'center' as const,
      width: 160,
      render: (createdAt: string) => {
        return (
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {dayjs(createdAt).format(ONLY_DATE_FORMAT)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dayjs(createdAt).format(ONLY_TIME_FORMAT)}
            </Typography>
          </Box>
        )
      },
    },
    {
      dataIndex: '',
      key: 'action',
      align: 'center' as const,
      width: 90,
      render: (_: undefined, pipeline: IPipeline) => {
        return (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              {pipeline?.leadObjectIds?.length === 0 && !pipeline.deletedAt && (
                <IconButton
                  onClick={() => {
                    setIsModalEditPipeline(true)
                    setPipelineEdit(pipeline)
                  }}
                >
                  <EditIcon />
                </IconButton>
              )}
              {!pipeline.deletedAt && (
                <IconButton onClick={() => onHandleDeletePipeline(pipeline)}>
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          </>
        )
      },
    },
  ]

  return (
    <>
      <PipelineFormModal
        isOpen={isModalEditPipeline}
        onUpdatePipelineListSuccess={onUpdatePipelineListSuccess}
        onClose={(visible: boolean) => setIsModalEditPipeline(visible)}
        pipeline={pipelineEdit}
      />
      <CircularLoading isLoading={isLoading || isLoadingDelete} />
      <Pagination
        total={total}
        current={filter.page!}
        pageSize={filter.perPage!}
        onChange={(page, perPage) =>
          handlePageChange({ ...filter, page, perPage })
        }
        mb={2}
      />
      <Table
        size={'middle'}
        columns={columns}
        scroll={{
          x: columns.reduce(
            (pre, cur: any) => (cur.width ? cur.width + pre : pre),
            0,
          ),
        }}
        expandable={{
          expandedRowRender: (record) => (
            <Stepper activeStep={-1} alternativeLabel>
              {record.items.map((step, index) => {
                return (
                  <Step key={index}>
                    <StepLabel>
                      <Typography variant="subtitle1" color="text.secondary">
                        {step.name}
                      </Typography>
                    </StepLabel>
                  </Step>
                )
              })}
            </Stepper>
          ),
          rowExpandable: (record) => record.name !== 'Not Expandable',
        }}
        dataSource={data?.map((pipeline: IPipeline, index: number) => {
          return {
            ...pipeline,
            key: index,
            numberRow:
              index + 1 + ((filter.page || 0) - 1) * (filter.perPage || 0),
          }
        })}
        bordered
        pagination={false}
      />
      <Pagination
        total={total}
        current={filter.page!}
        pageSize={filter.perPage!}
        onChange={(page, perPage) =>
          handlePageChange({ ...filter, page, perPage })
        }
      />
    </>
  )
}

export default PipelineTable
